import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  message,
  Modal,
  Space,
  Spin,
  Tag,
} from 'antd';
import { DeleteOutlined, EditOutlined, ShopOutlined } from '@ant-design/icons';
import { Breadcrumbs } from '../../components/layout/Breadcrumbs';
import { PromotionTypeTag } from '../../components/shop-promotions/PromotionTypeTag';
import {
  deleteShopPromotion,
  fetchShopPromotionById,
} from '../../services/shopPromotionsService';
import type { ShopPromotionRecord } from '../../types/shopPromotion';
import {
  formatDateTimeRange,
  formatPromotionValueSummary,
  parsePromotionMetadata,
  promotionTypeLabel,
} from '../../utils/shopPromotionLabels';
import { formatCurrencyVND } from '../../utils/transactionHelpers';
import api from '../../services/api';

function MetadataLines({ record }: { record: ShopPromotionRecord }) {
  const meta = parsePromotionMetadata(record.metadata);
  if (record.discount_type === 'bxgy') {
    const buy = (meta.buy_items ?? meta.buyItems) as
      | Array<{ food_item_id?: number; quantity?: number }>
      | undefined;
    const gift = (meta.gift_items ?? meta.giftItems) as
      | Array<{ food_item_id?: number; quantity?: number }>
      | undefined;
    return (
      <>
        <p>
          <strong>Món mua:</strong>{' '}
          {(buy ?? [])
            .map(b => `#${b.food_item_id} × ${b.quantity ?? 1}`)
            .join(', ') || '–'}
        </p>
        <p>
          <strong>Món tặng:</strong>{' '}
          {(gift ?? [])
            .map(g => `#${g.food_item_id} × ${g.quantity ?? 1}`)
            .join(', ') || '–'}
        </p>
      </>
    );
  }
  if (record.discount_type === 'combo') {
    const items = (meta.combo_items ?? meta.comboItems) as
      | Array<{ food_item_id?: number; quantity?: number }>
      | undefined;
    const price = Number(meta.combo_price ?? meta.comboPrice);
    return (
      <>
        <p>
          <strong>Giá combo:</strong>{' '}
          {Number.isFinite(price) ? formatCurrencyVND(price) : '–'}
        </p>
        <p>
          <strong>Món trong combo:</strong>{' '}
          {(items ?? [])
            .map(c => `#${c.food_item_id} × ${c.quantity ?? 1}`)
            .join(', ') || '–'}
        </p>
      </>
    );
  }
  return null;
}

export default function ShopPromotionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<ShopPromotionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [merchantId, setMerchantId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const numId = parseInt(id ?? '', 10);
    if (!Number.isFinite(numId)) {
      setError('ID không hợp lệ');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetchShopPromotionById(numId);
      if (res.success && res.data) {
        setRecord(res.data);
        setError(null);
      } else {
        setError(res.message || 'Không tìm thấy khuyến mãi');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Lỗi tải chi tiết');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!record?.restaurant_id) return;
    (async () => {
      try {
        const res = await api.get<{
          success: boolean;
          data: Array<{ id: number; restaurantId?: number }>;
        }>('/merchants', { params: { page: 1, limit: 500 } });
        if (!res.data.success) return;
        const match = (res.data.data ?? []).find(
          m => (m.restaurantId ?? m.id) === record.restaurant_id
        );
        if (match) setMerchantId(match.id);
      } catch {
        /* ignore */
      }
    })();
  }, [record?.restaurant_id]);

  const handleDelete = () => {
    if (!record) return;
    Modal.confirm({
      title: 'Xóa khuyến mãi?',
      content: record.is_active
        ? 'Vui lòng tắt khuyến mãi trước khi xóa.'
        : `Xóa mã ${record.code}?`,
      okText: 'Xóa',
      okType: 'danger',
      onOk: async () => {
        try {
          const res = await deleteShopPromotion(record.id);
          if (res.success) {
            message.success('Đã xóa');
            navigate('/shop-promotions');
          } else {
            message.error(res.message || 'Xóa thất bại');
          }
        } catch (e: unknown) {
          const ax = e as { response?: { data?: { message?: string } } };
          message.error(ax.response?.data?.message || 'Xóa thất bại');
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !record) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={error || 'Không tìm thấy'} />
        <Button
          style={{ marginTop: 16 }}
          onClick={() => navigate('/shop-promotions')}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs
        items={[
          { label: 'Trang chủ', path: '/' },
          { label: 'KM quán', path: '/shop-promotions' },
          { label: record.code },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>{record.name || record.code}</h2>
          <Space style={{ marginTop: 8 }}>
            <PromotionTypeTag type={record.discount_type} />
            <Tag color={record.is_active ? 'success' : 'default'}>
              {record.is_active ? 'Đang bật' : 'Đã tắt'}
            </Tag>
          </Space>
        </div>
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/shop-promotions/${record.id}/edit`)}
          >
            Sửa
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            Xóa
          </Button>
        </Space>
      </div>

      <Card title="Thông tin chung">
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Mã">{record.code}</Descriptions.Item>
          <Descriptions.Item label="Loại">
            {promotionTypeLabel(record.discount_type)}
          </Descriptions.Item>
          <Descriptions.Item label="Giá trị / tóm tắt">
            {formatPromotionValueSummary(record)}
          </Descriptions.Item>
          <Descriptions.Item label="Quán (shop)">
            {record.restaurant_id != null ? (
              <Link to={`/merchants/${merchantId ?? record.restaurant_id}`}>
                <ShopOutlined />{' '}
                {record.restaurant_name ?? `Quán #${record.restaurant_id}`}
              </Link>
            ) : (
              '–'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Nguồn">
            {record.source_type ?? 'MERCHANT'}
          </Descriptions.Item>
          <Descriptions.Item label="Thời gian">
            {formatDateTimeRange(record.valid_from, record.valid_until)}
          </Descriptions.Item>
          <Descriptions.Item label="Đơn tối thiểu">
            {record.minimum_order_amount != null
              ? formatCurrencyVND(Number(record.minimum_order_amount))
              : '–'}
          </Descriptions.Item>
          <Descriptions.Item label="Đã dùng">
            {record.total_used ?? 0}
            {record.max_total_usage != null
              ? ` / ${record.max_total_usage}`
              : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Mô tả">
            {record.description || '–'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {(record.discount_type === 'bxgy' ||
        record.discount_type === 'combo') && (
        <Card title="Chi tiết KM" style={{ marginTop: 16 }}>
          <MetadataLines record={record} />
        </Card>
      )}
    </div>
  );
}
